import marked from "marked"
import sanitizeHtml from "sanitize-html"
import { Dated, Id, Parent } from "../data/models"

export function isDevMode() {
    return process.env.NODE_ENV !== 'production'
}

export function lastItem<T>(list: T[]): T {
    if (list.length <= 0) {
        throw Error(`Can't get the last item of an empty list`)
    }

    return list[list.length - 1]
}

export function assertDefined<T>(value: T, info?: string): asserts value {
    if (value == null) {
        throw Error(joinLines([
            `Expected a value to be defined, got undefined`,
            (info != null) ? `| Info: ${info}` : ''
        ]))
    }
}

export function assertAndCastBoolean(value: any): boolean | undefined {
    if (typeof value === 'boolean') {
        return value
    } else if (typeof value === 'string' && (value === 'true' || value === 'false')) {
        return value === 'true'
    } else if (value === undefined) {
        return undefined
    } else {
        throw new Error('Value is not a boolean, a string boolean, or undefined')
    }
}

/**
 * Expects a boolean expression that will evaluate to true. Otherwise
 * an error with the given message will be thrown.
 */
export function assertInvariant(invariant: boolean, ...errorMessage: string[]) {
    if (invariant === false) {
        const message = Array.isArray(errorMessage)
            ? errorMessage.join(` `)
            : errorMessage

        throw Error(joinLines([
            `Invariant violation! -`,
            message
        ]))
    }
}

export function sortLinkedList<T extends Id & Parent>(list: T[]) {
    const sortedList: T[] = []
    let currentChildId: string | undefined

    for (const item of list) {
        // Find the last item first
        if (item.childId == null) {
            currentChildId = item.id
            sortedList.push(item)
        }
    }

    outer: while (true) {
        for (const item of list) {
            if (item.childId === currentChildId) {

                currentChildId = item.id
                sortedList.push(item)

                continue outer
            }
        }

        break
    }

    if (sortedList.length !== list.length) {
        throw Error([
            `Failed to sort the entire list, check for`,
            `broken links in the list, input:\n${JSON.stringify(list)}`
        ].join(" "))
    }

    return sortedList.reverse()
}

/**
 * Returns a random integer between min (inclusive) and max (inclusive). The value is no
 * lower than min (or the next integer greater than min if min isn't an integer) and no
 * greater than max (or the next integer lower than max if max isn't an integer).
 *
 * Using Math.round() will give you a non-uniform distribution!
 *
 * https://stackoverflow.com/a/1527820/1891491
 */
export function getRandomInt(min: number, max: number) {
    min = Math.ceil(min)
    max = Math.floor(max)

    return Math.floor(Math.random() * (max - min + 1)) + min
}

/**
 * Helper function for custom buttons, captures space and enter
 */
export function onKbClick(cb?: () => void) {
    return (e: React.KeyboardEvent) => {
        if (e.key === " " || e.key === "Enter") {
            e.preventDefault()
            cb?.()
        }
    }
}

/**
 * Helper function to capture the enter click in an input.
 */
export function onEnter(cb?: () => void) {
    return (e: React.KeyboardEvent) => {
        if (e.key === "Enter") {
            e.preventDefault()
            cb?.()
        }
    }
}

/**
 * Shuffles array in place. ES6 version
 * @param {Array} array items An array containing the items.
 *
 * https://stackoverflow.com/a/6274381/1891491
 */
export function shuffleArray<T>(array: T[]): T[] {
    for (let i = array.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [array[i], array[j]] = [array[j], array[i]]
    }

    return array
}

export function expectOneById<T extends Id>(list: T[], id: string, message?: string): T {
    if (id == null) {
        throw Error(`Illegal param, id cannot be undefined`)
    }

    const item = list.filter(item => item.id === id)

    if (item.length !== 1) {
        throw Error([
            (message != null ? `${message} —` : ''),
            `Expected 1 item for id ${id}`,
            `found ${item.length}`
        ].join(' '))
    }

    return item[0]
}

export function expectOneByPredicate<T>(list: T[], predicate: (item: T) => boolean, message?: string): T {
    if (predicate == null) {
        throw Error(`Illegal param, predicate cannot be undefined`)
    }

    const item = list.filter(predicate)

    if (item.length !== 1) {
        throw Error([
            (message != null ? `${message} —` : ''),
            `Expected 1 item matching predicate`,
            `found ${item.length}`
        ].join(' '))
    }

    return item[0]
}


export function tryGetOneById<T extends Id>(list: T[], id: string): T | undefined {
    if (id == null) {
        throw Error(`Illegal param, id cannot be undefined`)
    }

    const matches = list.filter(item => item.id === id)

    if (matches.length >= 1) {
        return matches[0]
    }

    return undefined
}

export function joinLines(parts: string[], separator = " "): string {
    return parts.join(separator)
}

export function parseMarkdown(markdown: string) {
    // Make links open in new tabs
    // https://github.com/markedjs/marked/issues/655#issuecomment-383226346
    const renderer = new marked.Renderer()
    const linkRenderer = renderer.link
    renderer.link = (href, title, text) => {
        const html = linkRenderer.call(renderer, href, title, text)
        return html.replace(/^<a /, '<a target="_blank" rel="nofollow" ')
    }

    return sanitizeHtml(marked(markdown, { renderer }))
}

export function getRandomArrayValue<T>(list: T[]): T {
    return list[getRandomInt(0, list.length - 1)]
}

/**
 * Maps all array values as props in an object. Avoid duplicate keys for obvious reasons.
 */
export function listToObject<T extends Id>(list: T[]) {
    return list.reduce<{ [key: string]: T }>((acc, curr) => {

        const key = curr.id

        if (acc[key] != null) {
            throw Error(`Duplicate key detected, ${key}`)
        }

        acc[key] = curr
        return acc

    }, {})
}

export function sortByDateCreated<T extends Dated>(list: T[], order?: "asc" | "desc"): T[] {
    const sortedList = [...list].sort((a, b) => a.createdAt.localeCompare(b.createdAt))

    if (order === "desc") {
        return sortedList.reverse()
    }

    return sortedList
}

export function sortByDateUpdated<T extends Dated>(list: T[], order?: "asc" | "desc"): T[] {
    const sortedList = [...list].sort((a, b) => a.updatedAt.localeCompare(b.updatedAt))

    if (order === "desc") {
        return sortedList.reverse()
    }

    return sortedList
}

/**
 * For-each function that can handle two lists at once.
 *
 * Accepts two lists, a subset and a superset. The subset MUST ONLY
 * contain items also found in the superset.
 *
 * It then iterates the subset and calls the given callback with the
 * item found in the subset, and superset.
 */
export function forEachPair<A extends Id, B extends Id>(
    subset: A[],
    superset: B[],
    callback: (itemA: A, itemB: B) => void
): void {

    if (subset.length > superset.length) {
        throw Error(`The first list cannot be larger than the second`)
    }

    subset.forEach(itemA => {
        const itemB = superset.find(itemB => itemA.id === itemB.id)

        if (itemB == null) {
            throw Error(`Items in the subset (first list) MUST exist in superset (second list)`)
        }

        callback(itemA, itemB)
    })

}

/**
 * Helper function to grab things that have names
 */
export function getByName<T extends { name: string }>(name: string, list: T[]): T {
    const foundItems = list.filter(seg => seg.name === name)

    if (foundItems.length !== 1) {
        throw Error(joinLines([
            `Expected 1 match for name [${name}], found ${foundItems.length},`,
            `won't return anything. Make sure you use unique names.`
        ]))
    }

    return foundItems[0]
}

export function extractOneFromList<T>(list: T[], predicate: (item: T) => boolean): {
    match: T
    restOfList: T[]
} {
    return list.reduce<{ match: T; restOfList: T[] }>((acc, curr) => {

        if (predicate(curr)) {
            if (acc.match != null) {
                throw Error(`Expected exactly 1 match, found several: [${list}]`)
            }

            return {
                ...acc,
                match: curr,
            }
        } else {
            acc.restOfList.push(curr)
            return acc
        }

    }, {
        match: undefined as unknown as T,
        restOfList: []
    })
}

export function splitList<T>(list: T[], predicate: (item: T) => boolean): { taken: T[]; remaining: T[] } {
    const piles: { taken: T[]; remaining: T[] } = {
        taken: [],
        remaining: [],
    }

    list.forEach(item => {
        if (predicate(item)) {
            piles.taken.push(item)
        } else {
            piles.remaining.push(item)
        }
    })

    return piles
}

/**
 * splitList variant that works off of a type guard predicate, use
 * when the items you're taking are of a different type than the whole
 * list.
 *
 * The predicate function MUST be a TS type guard function, i.e.
 * return is `param1 is Type1` and not a boolean.
 */
export function splitListByType<T1, T2 = T1>(
    list: T1[],
    predicate: (item: T1 | T2) => item is T2
): {
    taken: T2[]
    remaining: T1[]
} {
    const piles: { taken: T2[]; remaining: T1[] } = {
        taken: [],
        remaining: [],
    }

    list.forEach(item => {
        if (predicate(item)) {
            piles.taken.push(item)
        } else {
            piles.remaining.push(item)
        }
    })

    return piles
}

export function getOrDefault<T>(maybeValue: T | undefined, defaultValue: T): T {
    if (maybeValue != null) {
        return maybeValue
    } else {
        return defaultValue
    }
}

export type GroupedList<T> = {
    /**
     * Returns a list for the given id, or an empty list if the given ID
     * isn't present
     */
    get(id: string): T[]
}

/**
 * Groups the given list on the ID produced by the given ID-resolver
 */
export function groupList<T>(
    list: T[],
    idResolver: (item: T) => string
): GroupedList<T> {
    const groupedList = list.reduce<{ [key: string]: T[] }>((acc, curr) => {

        const currId = idResolver(curr)
        const item = acc[currId]

        if (item == null) {
            acc[currId] = []
        }

        acc[currId].push(curr)

        return acc

    }, {})

    return {
        get: (id: string) => {
            return groupedList[id] ?? []
        },
    }
}

export type MappedList<T> = {
    /**
     * Returns the item for the given id,
     * or undefined if no item can be found
     */
    get(id: string): T | undefined

    /**
     * Returns the item for the given id, or throws an error if none can be found
     */
    getOrFail(id: string): T

    /**
     * Returns the item for the given id, or the defaultValue if no item
     * can be found
     */
    getOrDefault(id: string, defaultValue: T): T
}

/**
* Converts the given list to a map, keyed on its `id` parameter.
* Duplicate keys will cause an exception. Use `groupList` if your list
* has duplicate keys.
*
* @example
* const map = mapList([
*  {id: "1", value: "A"},
*  {id: "2", value: "B"},
*  {id: "3", value: "C"},
* ])
*
 * map.get("2") // => {id: "2", value: "B"}
*
 * @param list The list to map
*/
export function mapList<T extends { id: string }>(list: T[]): MappedList<T> {
    const mappedList = list.reduce<{ [key: string]: T }>((acc, curr) => {

        const item = acc[curr.id]

        if (item == null) {
            acc[curr.id] = curr
        } else {
            throw Error(`Item with id [${curr.id}] is already mapped`
                + `and cannot be mapped again, use groupList instead`)
        }

        return acc

    }, {})

    return {
        get: (id) => mappedList[id],

        getOrDefault: (id, defaultValue) => mappedList[id] ?? defaultValue,

        getOrFail: (id) => {
            if (mappedList[id] != null) {
                return mappedList[id]
            }

            throw Error(`No item for id: ${id}`)
        }
    }
}

/**
 * Never use with objects that contain functions.
 */
export function jsonClone<T>(obj: T) {
    return JSON.parse(JSON.stringify(obj)) as T
}

/**
 * Give a list of arrays, returns true if all of them are empty.
 */
export function areArraysEmpty<T>(arrays: T[][]): boolean {
    for (let index = 0; index < arrays.length; index++) {
        const array = arrays[index]

        if (array.length > 0) {
            return false
        }
    }

    return true
}

/**
 * Only adds the item if it can't be found, uses .includes for the
 * comparison.
 */
export function addUniqueToArray(array: string[], item: string) {
    if (array.includes(item)) {
        return array
    }

    return [
        ...array,
        item
    ]
}

export function removeFromArray(array: string[], item: string) {
    return array.filter(i => i !== item)
}

export function removeFromArrayById<T extends Id>(array: T[], items: T[]) {
    return array.filter(item => {
        return items.filter(innerItem => innerItem.id === item.id).length === 0
    })
}

/**
 * Wrapper around join with a default delimiter.
 */
export function concat(parts: string[]) {
    return parts.join("¤")
}

/**
 * Checks if the given string is blank (after trims) and returns
 * undefined if it is.
 *
 * Otherwise it returns the string AFTER trimming it.
 */
export function emptyStringAsUndefined(value?: string): string | undefined {
    if (value == null) {
        return undefined
    }

    if (value.trim().length <= 0) {
        return undefined
    }

    return value.trim()
}

export function getQueryParamValue({ query, paramName }: {
    /**
     * `window.location.search`
     */
    query: string
    /**
     * The name of the parameter to get
     */
    paramName: string
}): string | undefined {

    if (query === "") {
        return undefined
    }

    const parts = query
        .substring(1) // Leading questionmark
        .split("&")

    const value = parts
        .find(item => item.startsWith(paramName))
        ?.split("=")[1]

    if (value == null) {
        return undefined
    }

    return value
}

export function getQueryParamValues<T>({ query, paramName }: {
    /**
     * `window.location.search`
     */
    query: string
    /**
     * The name of the parameter to get
     */
    paramName: string
}): T[] | undefined {

    if (query === "") {
        return undefined
    }

    const parts = query
        .substring(1) // Leading questionmark
        .split("&")

    const keyedParts = parts.reduce<{ [key: string]: T[] }>((acc, curr) => {
        const [key, value] = curr.split("=")

        const safeValue = emptyStringAsUndefined(value) ?? true

        if (acc[key] != null) {
            acc[key].push(safeValue as any)
        } else {
            acc[key] = [safeValue as any]
        }

        return acc
    }, {})

    const maybeValues = keyedParts[paramName]

    if (maybeValues == null) {
        return undefined
    }

    return maybeValues
}

/**
 * Replaces the given item in the list, if the item doesn't exist,
 * it's simply added.
 *
 * Does not mutate the given list, a new list is returned. Order is
 * maintained.
 */
export function updateListItem<T extends Id>(list: T[], item: T) {
    const itemIndex = list.findIndex(i => i.id === item.id)

    if (itemIndex < 0) {
        return [...list, item]
    }

    const updatedList = [...list]
    updatedList.splice(itemIndex, 1, item)

    return updatedList
}

export function isNonEmptyString(value: unknown) {
    if (value == null) {
        return false
    }

    if (typeof value === "string" && value !== "") {
        return true
    }

    return false
}

/**
 * Creates a new type where prop T on object K is required and
 * non-nullable.
 */
type DefinedProp<T, K extends keyof T> = (
    Omit<T, K> &
    { [key in K]-?: Exclude<T[key], undefined> }
)

/**
 * For the given object and key, ensures the property at the given key
 * is defined and forces the type to reflect that.
 *
 * It's basically a nested `assertDefined` with a return. If there's
 * an easier way to get TS to remember scoped null checks on nested
 * properties I'd like to know about it.
 *
 * @example
 * type City = { street: string, zip: number }
 *
 * // Notice 'city' is optional, we're going to ensure that is defined
 * type Data = { name: string, city?: City | undefined }
 *
 * const data: Data = {
 *     name: 'Bob',
 *     city: {
 *         street: 'Some street',
 *         zip: 12345
 *     }
 * }
 *
 * const withCityDefined = assumeDefined(data, 'city')
 *
 * // 'city' is guaranteed to be defined here
 * console.log(withCityDefined.city.street)
 */
export function assumeDefined<T, K extends keyof T>(obj: T, key: K): DefinedProp<T, K> {
    if (obj[key] == null) {
        throw Error(`Property ${String(key)} in ${JSON.stringify(obj)} was not defined`)
    }

    return obj as unknown as DefinedProp<T, K>
}

/**
 * Returns the given callback if the given condition evaluates to
 * true, returns undefined otherwise.
 *
 * Created to deal with optional handlers in JSX in a less verbose
 * way.
 */
export function conditionalCallback<T>(condition: boolean, callback: T): T | undefined {
    if (condition) {
        return callback
    }
}

export type IdMapper = ReturnType<typeof idMapper>

export function idMapper<T extends { id: string }>(idGenerator: () => string) {

    const idMap: Map<string, string> = new Map()

    return {
        newId: (entity: T): string => {
            const newId = idGenerator()
            idMap.set(entity.id, newId)

            return newId
        },
        /**
         * Returns the new ID for the given old ID, used by
         * referencing entities to resolve their new relationship.
         *
         * Throws an error if a match cannot be made.
         */
        swap: (oldId: string): string => {
            const newId = idMap.get(oldId)

            if (newId == null) {
                throw Error(`Failed to find new id for ${oldId}`)
            }

            return newId
        },

        /**
         * Returns the new ID for the given old ID, used by
         * referencing entities to resolve their new relationship.
         *
         * Returns undefined if no match can be made.
         */
        trySwap: (oldId: string): string | undefined => {
            const newId = idMap.get(oldId)

            if (newId == null) {
                return undefined
            }

            return newId
        },

        /**
         * Returns true if the list of old IDs contains the given ID.
         */
        containsOldId: (oldId: string): boolean => {
            return idMap.has(oldId)
        }

    }
}

/**
 * Moves the index in either direction, if min/max is reached the
 * index loops around. Useful for navigating lists that should loop
 * around.
 */
export function updateIndexCircular(curr: number | null, min: number, max: number, dir: 'up' | 'down') {
    if (curr == null) {
        return dir === 'up' ? max : min
    }

    if (dir === 'up') {
        curr = curr === min ? max : curr - 1
    } else {
        curr = curr === max ? min : curr + 1
    }

    return curr
}
